import { FormValidator } from "@CubeComponents/Mui_Form/FormValidator";
import type { FormValidatorOptions, ValidationRule } from "@CubeComponents/Mui_Form/FormValidator";
import { presetSectionsCodeObject } from "@Helpers";
import { optionsViewType } from "@Features/portal-settings";
import { formStatuses } from "./constants";
import { FormControlLabel, RadioGroup } from "@mui/material";
import RadioButton from "@CubeComponents/Mui_Form/RadioButton";
import { getUserSelectionModeOptions } from "../FormFlow/formFlow.constants";
import type { ChangeEvent } from "react";
import type { QuestionField } from "@ServerTypes/forms/forms.schema";
import type { FormInstance, FormInstanceSection } from "../../formInstance.wire";

/**
 * The rule objects this helper builds. Deliberately NOT the validator's own `ValidationRule`:
 *
 * KNOWN BUG, preserved verbatim: `minLength` / `maxLength` are written below as bare numbers, but
 * `FormValidator.validateField` reads every rule as a `[value, message]` tuple
 * (`fieldRules.minLength as (number | string)[]`, then `arr[0]`). A bare number therefore yields
 * `arr[0] === undefined` and the length comparison is never true, so these two rules never fire.
 * The draft type permits the number so the emitted values stay byte-for-byte what they are today;
 * the fix is to write `[limit]` (and `[limit, message]`), which changes what the form rejects and
 * is a behaviour change, out of scope for a typing pass.
 */
type BuiltValidationRule = Omit<ValidationRule, "minLength" | "maxLength"> & {
  minLength?: number;
  maxLength?: number;
};

/** A file/folder row the caller wants a per-row copy of every rule for. */
interface ValidationTargetRow {
  id: string | number;
  isFolder?: boolean;
}

/** One row of a table section's `tableDataSource`; rules are keyed by `rowId` + question id. */
interface TableSectionRow {
  rowId?: string;
  [key: string]: unknown;
}

interface AddValidationToInputFieldsParams {
  /** The live form instance. When it has an `_id`, its current level decides which sections validate. */
  formData?: FormInstance | null;
  template?: FormInstanceSection[];
  /** Ref the constructed validator is stored on. */
  formObject: { current: FormValidator | null };
  rows?: ValidationTargetRow[];
  modifySection?: Array<string | undefined>;
}

export const statusTemplate = (stage?: string) => {
  let status = "";
  let color;
  if (stage == formStatuses.inProgress) {
    status = "In progress";
    color = "bg-system_colour_12";
  }
  if (stage == formStatuses.submitted) {
    status = "Submitted";
    color = "bg-system_colour_45";
  }
  if (stage == formStatuses.approved) {
    status = "Approved";
    color = "bg-positive_communication";
  }
  if (stage == formStatuses.rejected) {
    status = "Rejected";
    color = "bg-system_colour_8";
  }
  return (
    <div className={`h-8 ${color} w-fit ps-3 gap-2 bg-opacity-30 rounded-2xl pe-3 flex items-center`}>
      <span className={`${color} rounded-full h-3 w-3`} />
      <span className="font-semibold">{status}</span>
    </div>
  );
};
export const addValidationToInputFields = ({
  formData,
  template = [],
  formObject,
  rows = [],
  modifySection = [],
}: AddValidationToInputFieldsParams) => {
  const allSectionsInCurrentLevel: Array<string | undefined> = [];
  const excludedTypes = ["users", "fileUpload", "image", "url"];
  if (formData && formData._id) {
    // Non-null throughout this branch: the level at `currentLevelOfForm`, its `dataSource` and each
    // row's `sections` are all read without a guard. The first is `noUncheckedIndexedAccess`
    // bookkeeping; the other two are `.optional()` on the wire and dereferenced anyway, so an
    // absent value throws TypeError. All three assertions are erased at emit, keeping the existing
    // runtime; guarding them would mean inventing an empty-rule path — a behaviour change.
    formData.formFlow[formData.currentLevelOfForm]!.dataSource!.map((data) => {
      if (data.type === "Fill") {
        data.sections!.map((section) => allSectionsInCurrentLevel.push(section._id));
      } else if (data.type === "Modify") {
        data.sections!.map((section) => {
          if (modifySection.includes(section._id)) {
            allSectionsInCurrentLevel.push(section._id);
          }
        });
      }
    });
    template = formData.selectedForm.template;
  } else if (template && template.length) {
    template?.forEach((section) => allSectionsInCurrentLevel.push(section._id));
  } else if (formData && formData.selectedForm) {
    template = formData.selectedForm.template || [];
    template?.forEach((section) => allSectionsInCurrentLevel.push(section._id));
  }

  const options: { rules: Record<string, BuiltValidationRule> } = { rules: {} };
  template?.some((section) => {
    if (
      section.sectionCode == presetSectionsCodeObject.SECTION_TYPE_TABLE_SECTION &&
      allSectionsInCurrentLevel.includes(section._id)
    ) {
      // KNOWN BUG, preserved verbatim: `questionFields` and `tableDataSource` are `.optional()` on
      // the wire but dereferenced unconditionally in this table-section branch (the normal-section
      // branch below does guard `section.questionFields`), so a table section missing either throws
      // TypeError. Asserted (erased at emit) to keep the existing runtime; the real fix is a guard
      // plus a skip path — a behaviour change, out of scope for a typing pass.
      section.questionFields!.some((eachField: QuestionField) => {
        section.tableDataSource!.some((row: TableSectionRow) => {
          const isSelectWithListView =
            (eachField.fieldType === "multi-select" || eachField.fieldType === "single-select") &&
            eachField.viewType === optionsViewType.LIST;
          if (!excludedTypes.includes(eachField.fieldType) && !isSelectWithListView) {
            const ruleObj: BuiltValidationRule = {};
            const ruleName = `${row.rowId}${eachField._id}`;
            if (eachField.isMandatory === true) {
              ruleObj.required = [true, "* Required"];
            }
            if (["single-line", "multi-line"].includes(eachField.fieldType)) {
              ruleObj.minLength = eachField.minFieldLimit ?? 0;
              if (eachField.maxFieldLimit) {
                ruleObj.maxLength = eachField.maxFieldLimit;
              }
            } else if (["date-time"].includes(eachField.fieldType)) {
              ruleObj.regex = [
                "^([0-2][0-9]|3[0-1])/((0[1-9])|(1[0-2]))/([1-9][0-9]{3}) ((0[1-9])|(1[0-2])):([0-5][0-9]):(AM|PM)$",
                "Please enter a valid date",
              ];
            }
            options.rules[ruleName] = {
              ...ruleObj,
            };
            if (rows && Array.isArray(rows)) {
              rows.forEach((row) => {
                if (!row.isFolder) {
                  options.rules[`${row.id}_${ruleName}`] = {
                    ...ruleObj,
                  };
                }
              });
            } else {
              options.rules[ruleName] = {
                ...ruleObj,
              };
            }
          }
        });
      });
    } else if (section.questionFields) {
      if (allSectionsInCurrentLevel.includes(section._id)) {
        section.questionFields.some((eachField: QuestionField) => {
          const isSelectWithListView =
            (eachField.fieldType === "multi-select" || eachField.fieldType === "single-select") &&
            eachField.viewType === optionsViewType.LIST;
          if (!excludedTypes.includes(eachField.fieldType) && !isSelectWithListView) {
            const ruleName = `${eachField._id}`;
            const ruleObj: BuiltValidationRule = {};
            if (eachField.isMandatory === true) {
              ruleObj.required = [true, "* Required"];
            }
            if (["single-line", "multi-line"].includes(eachField.fieldType)) {
              ruleObj.minLength = eachField.minFieldLimit ?? 0;
              if (eachField.maxFieldLimit) {
                ruleObj.maxLength = eachField.maxFieldLimit;
              }
            } else if (["date-time"].includes(eachField.fieldType)) {
              ruleObj.regex = [
                "^([0-2][0-9]|3[0-1])/((0[1-9])|(1[0-2]))/([1-9][0-9]{3}) ((0[1-9])|(1[0-2])):([0-5][0-9]):(AM|PM)$",
                "Please enter a valid date",
              ];
            }
            if (rows && Array.isArray(rows)) {
              rows.forEach((row) => {
                if (!row.isFolder) {
                  options.rules[`${row.id}_${ruleName}`] = {
                    ...ruleObj,
                  };
                }
              });
            } else {
              options.rules[ruleName] = {
                ...ruleObj,
              };
            }
          }
        });
      }
    }
    // `options` carries the bare-number length rules documented on `BuiltValidationRule`; the cast
    // is erased at emit and hands the validator exactly the object it gets today.
    formObject.current = new FormValidator("#formData", options as FormValidatorOptions);
  });
};
/**
 * "Who should be assigned?" radios. The options themselves live in
 * `FormFlow/formFlow.constants` so web and mobile offer exactly the same choices.
 */
export const getUserSelection = (
  heading: string,
  userSelectionMode: string,
  handleUserSelectionModeChange: (args: ChangeEvent<HTMLInputElement>) => void,
) => (
  <div className="flex flex-col gap-2">
    <span>{heading}</span>
    <span>
      <RadioGroup value={userSelectionMode} onChange={handleUserSelectionModeChange}>
        {getUserSelectionModeOptions().map((option) => (
          <FormControlLabel key={option.value} value={option.value} control={<RadioButton />} label={option.label} />
        ))}
      </RadioGroup>
    </span>
  </div>
);
